import { ReactNode, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { EVENT_NAME } from '../constants/branding'
import { fetchCurrentUser, isAdminUser, type AuthenticatedUser } from '../services/auth'
import './Layout.css'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(null)
  const isGamesSectionActive = ['/group-play', '/playoffs', '/bracket-play'].includes(location.pathname)

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await fetchCurrentUser()
        setCurrentUser(user)
      } catch {
        setCurrentUser(null)
      }
    }

    loadCurrentUser()
  }, [])

  return (
    <div className="layout">
      <div className="layout-backdrop" aria-hidden="true" />
      <header className="header">
        <div className="container">
          <Link to="/" className="logo-link">
            <div className="brand-copy">
              <span className="brand-kicker">Tournament Event</span>
              <span className="brand-name">{EVENT_NAME}</span>
            </div>
            <img
              src="/prophet-topbar-logo.png"
              alt={EVENT_NAME}
              className="logo"
              onError={(e) => {
                const target = e.currentTarget
                target.style.display = 'none'
                const fallback = target.nextElementSibling as HTMLElement
                if (fallback) {
                  fallback.style.display = 'block'
                }
              }}
            />
            <span className="logo-fallback" style={{ display: 'none' }}>
              {EVENT_NAME}
            </span>
          </Link>
          <nav className="nav">
            <Link 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
            >
              Dashboard
            </Link>
            <Link 
              to="/upload" 
              className={location.pathname === '/upload' ? 'active' : ''}
            >
              Upload Results
            </Link>
            <Link 
              to="/standings" 
              className={location.pathname === '/standings' ? 'active' : ''}
            >
              Standings
            </Link>
            <div className={`nav-menu ${isGamesSectionActive ? 'active' : ''}`}>
              <Link
                to="/group-play"
                className={`nav-menu-trigger ${isGamesSectionActive ? 'active' : ''}`}
                aria-haspopup="true"
              >
                Games
              </Link>
              <div className="nav-submenu" aria-label="Games submenu">
                <Link
                  to="/group-play"
                  className={location.pathname === '/group-play' ? 'active' : ''}
                >
                  Group Play
                </Link>
                <Link
                  to="/playoffs"
                  className={location.pathname === '/playoffs' ? 'active' : ''}
                >
                  Playoffs
                </Link>
                <Link
                  to="/bracket-play"
                  className={location.pathname === '/bracket-play' ? 'active' : ''}
                >
                  Bracket
                </Link>
              </div>
            </div>
            <Link 
              to="/stats" 
              className={location.pathname === '/stats' ? 'active' : ''}
            >
              Stats
            </Link>
            <Link 
              to="/account"
              className={location.pathname === '/account' ? 'active' : ''}
            >
              Account
            </Link>
          </nav>
        </div>
      </header>
      <main className="main">
        <div className="container">
          {children}
        </div>
      </main>
      <footer className="footer">
        <div className="container">
          <p>
            &copy; 2026 {EVENT_NAME}
          </p>
          {isAdminUser(currentUser) && (
            <Link
              to="/admin"
              className={`footer-admin-link ${location.pathname === '/admin' ? 'active' : ''}`}
            >
              Admin
            </Link>
          )}
        </div>
      </footer>
    </div>
  )
}

